import React from 'react';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import type { ConfigValue, PipelineSourceField, StepConfig } from './types';
import { CAST_TYPE_OPTIONS, DATE_FORMAT_PRESETS, IF_OPERATOR_OPTIONS } from './functionRegistry';

interface StepConfigPanelProps {
  functionName: string;
  config: StepConfig;
  sourceFields: PipelineSourceField[];
  onChange: (config: Partial<StepConfig>) => void;
}

const ConfigValueInput: React.FC<{
  value: ConfigValue;
  sourceFields: PipelineSourceField[];
  onChange: (v: ConfigValue) => void;
  placeholder?: string;
  label?: string;
}> = ({ value, sourceFields, onChange, placeholder, label }) => {
  if (value.kind === 'field') {
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        {label && (
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 50 }}>
            {label}
          </Typography>
        )}
        <Select
          size="small"
          value={value.fieldId}
          onChange={(e) => {
            const sf = sourceFields.find((f) => f.id === e.target.value);
            if (sf) {
              onChange({ kind: 'field', fieldId: sf.id, fieldName: sf.sourceField });
            }
          }}
          sx={{ minWidth: 100, fontSize: '0.8rem' }}
        >
          {sourceFields.map((sf) => (
            <MenuItem key={sf.id} value={sf.id}>
              {sf.sourceField}
            </MenuItem>
          ))}
        </Select>
        <Tooltip title="Switch to text value">
          <IconButton
            size="small"
            onClick={() => onChange({ kind: 'literal', value: value.fieldName })}
          >
            <Typography variant="caption" sx={{ fontSize: 10 }}>Aa</Typography>
          </IconButton>
        </Tooltip>
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 50 }}>
          {label}
        </Typography>
      )}
      <TextField
        size="small"
        value={value.value}
        onChange={(e) => onChange({ kind: 'literal', value: e.target.value })}
        placeholder={placeholder}
        sx={{ flex: 1, '& input': { fontSize: '0.8rem', py: 0.5 } }}
      />
      {sourceFields.length > 0 && (
        <Tooltip title="Use a field instead">
          <IconButton
            size="small"
            onClick={() => {
              const first = sourceFields[0];
              onChange({ kind: 'field', fieldId: first.id, fieldName: first.sourceField });
            }}
          >
            <Typography variant="caption" sx={{ fontSize: 10 }}>&#123;&#125;</Typography>
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

const MultiValueConfig: React.FC<{
  label: string;
  values: ConfigValue[];
  sourceFields: PipelineSourceField[];
  onChange: (values: ConfigValue[]) => void;
  addLabel?: string;
}> = ({ label, values, sourceFields, onChange, addLabel = 'Add value' }) => {
  const handleUpdate = (index: number, v: ConfigValue) => {
    const next = [...values];
    next[index] = v;
    onChange(next);
  };
  const handleRemove = (index: number) => {
    onChange(values.filter((_, i) => i !== index));
  };
  const handleAdd = () => {
    onChange([...values, { kind: 'literal', value: '' }]);
  };

  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {values.map((v, i) => (
        <Stack key={i} direction="row" spacing={0.5} alignItems="center">
          <Typography variant="caption" color="text.disabled" sx={{ minWidth: 16 }}>
            {i + 1}.
          </Typography>
          <Box sx={{ flex: 1 }}>
            <ConfigValueInput
              value={v}
              sourceFields={sourceFields}
              onChange={(updated) => handleUpdate(i, updated)}
              placeholder={`Value ${i + 1}`}
            />
          </Box>
          {values.length > 2 && (
            <IconButton size="small" onClick={() => handleRemove(i)}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          )}
        </Stack>
      ))}
      <Button
        size="small"
        startIcon={<AddIcon sx={{ fontSize: 14 }} />}
        onClick={handleAdd}
        sx={{ alignSelf: 'flex-start', fontSize: '0.7rem', textTransform: 'none' }}
      >
        {addLabel}
      </Button>
    </Stack>
  );
};

const StepConfigPanel: React.FC<StepConfigPanelProps> = ({
  functionName,
  config,
  sourceFields,
  onChange,
}) => {
  switch (functionName) {
    case 'UPPER':
    case 'LOWER':
    case 'TRIM':
    case 'ABS':
    case 'IS_NULL':
    case 'IS_NOT_NULL':
    case 'LENGTH':
      return (
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          Applies to the input automatically
        </Typography>
      );

    case 'CONCAT':
      return (
        <MultiValueConfig
          label="Parts to join"
          values={config.parts ?? [{ kind: 'literal', value: '' }, { kind: 'literal', value: '' }]}
          sourceFields={sourceFields}
          onChange={(parts) => onChange({ parts })}
          addLabel="Add part"
        />
      );

    case 'SPLIT':
      return (
        <Stack spacing={1}>
          <TextField
            size="small"
            label="Delimiter"
            value={config.delimiter ?? ','}
            onChange={(e) => onChange({ delimiter: e.target.value })}
            sx={{ '& input': { fontSize: '0.8rem' } }}
          />
          <TextField
            size="small"
            label="Element index"
            type="number"
            value={config.index ?? 0}
            onChange={(e) => onChange({ index: parseInt(e.target.value, 10) || 0 })}
            inputProps={{ min: 0 }}
            sx={{ '& input': { fontSize: '0.8rem' } }}
          />
        </Stack>
      );

    case 'REPLACE':
      return (
        <Stack spacing={1}>
          <TextField
            size="small"
            label="Find"
            value={config.find ?? ''}
            onChange={(e) => onChange({ find: e.target.value })}
            placeholder="Text to find"
            sx={{ '& input': { fontSize: '0.8rem' } }}
          />
          <TextField
            size="small"
            label="Replace with"
            value={config.replaceWith ?? ''}
            onChange={(e) => onChange({ replaceWith: e.target.value })}
            placeholder="Replacement text"
            sx={{ '& input': { fontSize: '0.8rem' } }}
          />
        </Stack>
      );

    case 'IF':
      return (
        <Stack spacing={1}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Condition
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
            <Box sx={{ flex: 1, minWidth: 80 }}>
              <ConfigValueInput
                value={config.conditionField ?? { kind: 'literal', value: '' }}
                sourceFields={sourceFields}
                onChange={(v) => onChange({ conditionField: v })}
                placeholder="field"
              />
            </Box>
            <Select
              size="small"
              value={config.operator ?? '='}
              onChange={(e) => onChange({ operator: e.target.value })}
              sx={{ minWidth: 80, fontSize: '0.8rem' }}
            >
              {IF_OPERATOR_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            {config.operator !== 'IS NULL' && config.operator !== 'IS NOT NULL' && (
              <TextField
                size="small"
                value={config.conditionValue ?? ''}
                onChange={(e) => onChange({ conditionValue: e.target.value })}
                placeholder="value"
                sx={{ flex: 1, minWidth: 60, '& input': { fontSize: '0.8rem' } }}
              />
            )}
          </Stack>
          <ConfigValueInput
            value={config.trueValue ?? { kind: 'literal', value: '' }}
            sourceFields={sourceFields}
            onChange={(v) => onChange({ trueValue: v })}
            placeholder="Value if true"
            label="Then"
          />
          <ConfigValueInput
            value={config.falseValue ?? { kind: 'literal', value: '' }}
            sourceFields={sourceFields}
            onChange={(v) => onChange({ falseValue: v })}
            placeholder="Value if false"
            label="Else"
          />
        </Stack>
      );

    case 'CAST':
      return (
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            Convert to
          </Typography>
          <Select
            size="small"
            value={config.targetType ?? 'INT'}
            onChange={(e) => onChange({ targetType: e.target.value })}
            sx={{ fontSize: '0.8rem' }}
          >
            {CAST_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </Stack>
      );

    case 'DATE_FORMAT':
    case 'PARSE_DATE':
    case 'TO_DATE':
    case 'TO_CHAR':
    case 'TO_TIMESTAMP':
      return (
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            {functionName === 'TO_DATE' || functionName === 'TO_TIMESTAMP'
              ? 'Date format (optional — leave empty for auto-detect)'
              : 'Format pattern'}
          </Typography>
          <Select
            size="small"
            value={config.format ?? '%Y-%m-%d'}
            onChange={(e) => onChange({ format: e.target.value })}
            sx={{ fontSize: '0.8rem' }}
          >
            {DATE_FORMAT_PRESETS.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </Select>
          <TextField
            size="small"
            label="Custom format"
            value={config.format ?? '%Y-%m-%d'}
            onChange={(e) => onChange({ format: e.target.value })}
            sx={{ '& input': { fontSize: '0.8rem', fontFamily: 'monospace' } }}
          />
        </Stack>
      );

    case 'ROUND':
      return (
        <TextField
          size="small"
          label="Decimal places"
          type="number"
          value={config.decimals ?? 2}
          onChange={(e) => onChange({ decimals: parseInt(e.target.value, 10) || 0 })}
          inputProps={{ min: 0, max: 10 }}
          sx={{ maxWidth: 140, '& input': { fontSize: '0.8rem' } }}
        />
      );

    case 'COALESCE':
      return (
        <MultiValueConfig
          label="Fallback values (first non-empty wins)"
          values={
            config.fallbacks ?? [
              { kind: 'literal', value: '' },
              { kind: 'literal', value: '' },
            ]
          }
          sourceFields={sourceFields}
          onChange={(fallbacks) => onChange({ fallbacks })}
          addLabel="Add fallback"
        />
      );

    case 'NVL':
      return (
        <MultiValueConfig
          label="Default value if input is empty"
          values={config.fallbacks ?? [{ kind: 'literal', value: '' }]}
          sourceFields={sourceFields}
          onChange={(fallbacks) => onChange({ fallbacks })}
          addLabel="Add fallback"
        />
      );

    case 'LITERAL':
      return (
        <TextField
          size="small"
          label="Constant value"
          value={config.literalValue ?? ''}
          onChange={(e) => onChange({ literalValue: e.target.value })}
          placeholder="Enter a fixed value"
          sx={{ '& input': { fontSize: '0.8rem' } }}
        />
      );

    case 'SUBSTRING':
      return (
        <Stack spacing={1}>
          <TextField
            size="small"
            label="Start position"
            type="number"
            value={config.start ?? 1}
            onChange={(e) => onChange({ start: parseInt(e.target.value, 10) || 1 })}
            inputProps={{ min: 1 }}
            sx={{ maxWidth: 140, '& input': { fontSize: '0.8rem' } }}
          />
          <TextField
            size="small"
            label="Length"
            type="number"
            value={config.length ?? 10}
            onChange={(e) => onChange({ length: parseInt(e.target.value, 10) || 1 })}
            inputProps={{ min: 1 }}
            sx={{ maxWidth: 140, '& input': { fontSize: '0.8rem' } }}
          />
        </Stack>
      );

    case 'LEFT':
    case 'RIGHT':
      return (
        <TextField
          size="small"
          label={`Number of characters from the ${functionName === 'LEFT' ? 'start' : 'end'}`}
          type="number"
          value={config.length ?? 5}
          onChange={(e) => onChange({ length: parseInt(e.target.value, 10) || 1 })}
          inputProps={{ min: 1 }}
          sx={{ maxWidth: 200, '& input': { fontSize: '0.8rem' } }}
        />
      );

    case 'LPAD':
    case 'RPAD':
      return (
        <Stack spacing={1}>
          <TextField
            size="small"
            label="Total length"
            type="number"
            value={config.padLength ?? 10}
            onChange={(e) => onChange({ padLength: parseInt(e.target.value, 10) || 1 })}
            inputProps={{ min: 1 }}
            sx={{ maxWidth: 140, '& input': { fontSize: '0.8rem' } }}
          />
          <TextField
            size="small"
            label="Pad character"
            value={config.padChar ?? (functionName === 'LPAD' ? '0' : ' ')}
            onChange={(e) => onChange({ padChar: e.target.value })}
            inputProps={{ maxLength: 1 }}
            sx={{ maxWidth: 140, '& input': { fontSize: '0.8rem', fontFamily: 'monospace' } }}
          />
        </Stack>
      );

    case 'CASE_WHEN': {
      const branches = config.branches ?? [{ condition: '', result: '' }];
      const handleBranchChange = (index: number, field: 'condition' | 'result', value: string) => {
        const updated = [...branches];
        updated[index] = { ...updated[index], [field]: value };
        onChange({ branches: updated });
      };
      const handleAddBranch = () => {
        onChange({ branches: [...branches, { condition: '', result: '' }] });
      };
      const handleRemoveBranch = (index: number) => {
        if (branches.length > 1) {
          onChange({ branches: branches.filter((_, i) => i !== index) });
        }
      };
      return (
        <Stack spacing={1}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            When input equals... then output...
          </Typography>
          {branches.map((b, i) => (
            <Stack key={i} direction="row" spacing={0.5} alignItems="center">
              <Typography variant="caption" color="text.disabled" sx={{ minWidth: 40 }}>
                When
              </Typography>
              <TextField
                size="small"
                value={b.condition}
                onChange={(e) => handleBranchChange(i, 'condition', e.target.value)}
                placeholder="value"
                sx={{ flex: 1, '& input': { fontSize: '0.8rem', py: 0.5 } }}
              />
              <Typography variant="caption" color="text.disabled" sx={{ minWidth: 32 }}>
                Then
              </Typography>
              <TextField
                size="small"
                value={b.result}
                onChange={(e) => handleBranchChange(i, 'result', e.target.value)}
                placeholder="output"
                sx={{ flex: 1, '& input': { fontSize: '0.8rem', py: 0.5 } }}
              />
              {branches.length > 1 && (
                <IconButton size="small" onClick={() => handleRemoveBranch(i)}>
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </Stack>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            onClick={handleAddBranch}
            sx={{ alignSelf: 'flex-start', fontSize: '0.7rem', textTransform: 'none' }}
          >
            Add condition
          </Button>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="caption" color="text.disabled" sx={{ minWidth: 40 }}>
              Else
            </Typography>
            <TextField
              size="small"
              value={config.elseResult ?? ''}
              onChange={(e) => onChange({ elseResult: e.target.value })}
              placeholder="default value"
              sx={{ flex: 1, '& input': { fontSize: '0.8rem', py: 0.5 } }}
            />
          </Stack>
        </Stack>
      );
    }

    default:
      return (
        <Typography variant="caption" color="text.secondary">
          No configuration available
        </Typography>
      );
  }
};

export default StepConfigPanel;
